import { useEffect, type ReactNode } from 'react';
import { Brain, FileText, ListChecks, Repeat2, Wrench } from 'lucide-react';
import { CallBadge, type CallRun, type Choice } from './CallBadge';
import { PromptPanel, usePromptCall } from './PromptBadge';
import { reasoningTooltip } from './ReasoningBadge';
import {
  builtWith, draftShotSrc, interactionsOfRun, passGroup, rebuiltOf, stagesOf, versionGroup,
  versionRootId, writtenCallOf, writtenPrompt,
  type WrittenCall, type WrittenInteraction, type WrittenPart, type WrittenSize,
} from './openaiBlenderApi';
import { type Job3D } from './threeDApi';
import type { Material } from './materialsRun';
import { DIRECTION_IDS, directionLabel } from './viewDirections';
import { partCropsOf, type PartCrop } from './productPartCrops';
import { cropOut } from './partCrop';
import { listingImageSrc } from './listingCropsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// THE WRITTEN ENGINE'S CALL, BEHIND THE BADGE EVERY OTHER CALL IN THIS APP SITS BEHIND.
//
// Phase 5's other engine is a reconstructor: it takes a picture and some numbers, and
// there is nothing to read. This one asks a language model a question in words and gets
// a program back, which makes it the same kind of thing as every Gemini call on the other
// phases — and those have all had this badge for as long as they have existed. So it is
// CallBadge and not a panel of its own: one icon, a menu, a modal that stays put.
//
// A ROW PER PASS, because a run is not one call: it is up to five, each shown what the last
// one built and asked to correct it. Every other badge in the app is one question and one
// answer, and this one was drawn that way too — a Prompt, an answer, and a "second round" in
// the middle — which held for exactly as long as a run could only be looked at twice. With
// five the middle is not a middle, and reading a pass means reading the two halves of it
// together anyway: what it was shown, and what it wrote back. So each pass is one row.
//
// TWO BADGES FROM ONE COMPONENT. Beside the button it is the NEXT press (`nextPress`): the
// question pass 1 will be asked, and the one every pass after it will be, both built from what
// is selected now. In the viewer it is the run on screen, one row per pass. The next press's
// badge used to carry the newest run's pass rows under its own, which were the modal's rows
// again about a run nobody was looking at.
export function WrittenCallBadge({
  subject, photos, run, runs, nextPress, model, size, parts, materials, crops, schema,
  interactions, title, press, lead = [], picker,
}: {
  // THE NEXT PRESS'S PICTURES AS A PICKER, in the prompt row where its photographs would be shown
  // (see PromptPanel). `crops`: whether it holds the close-ups too — a part's manual crops are in
  // its picker, a product's part crops are not — so the strip does not show them a second time.
  picker?: { node: ReactNode; crops: boolean };
  // THE GENERATION BADGE's: its name on the pill, the press itself, and the rows ahead of the
  // prompt's (the pictures and settings). See MeshGenerateControls.
  title?: string;
  press?: CallRun;
  lead?: Choice[];
  // What the thing in the photograph is called, when the page knows — the product's label
  // or the part's name. It goes into the question, so it goes into the panel.
  subject?: string;
  // THE PHOTOGRAPHS THIS BUTTON WOULD SEND, in the order it would send them, each with the
  // angle the page knows it to be of. Drawn by the panel at a size somebody can look at,
  // which is the point of them being here rather than described in the text — and the
  // angles are in the words too, because the prompt numbers the pictures and names each
  // one. A panel showing the one-picture sentence over a row of four would be describing a
  // call nobody is about to make.
  photos?: { url: string; view?: string }[];
  // WHICH RUN'S CALL THIS IS, in the viewer: the run ON SCREEN, which is the whole point of
  // it being there — eleven meshes in a strip are eleven different calls, and the one worth
  // reading is the one you are looking at. None beside the button (`nextPress`).
  run?: Job3D | null;
  // AND EVERY RUN IN THE SAME STRIP, so `run`'s other passes can be found — a pass is a row
  // of its own here and each is a separate run. Its own passes and not the strip: passGroup
  // walks the `draftJob` links back from this one.
  runs?: Job3D[];
  // THIS BADGE IS BESIDE THE BUTTON, where the question worth reading is the one nobody has
  // asked yet. The plain Prompt row is what the NEXT press would send — built from what is
  // selected now, not from what any run was given — so it belongs where the press is, and
  // nowhere else: in the viewer it would be a sixth row describing a call that is not the
  // mesh on screen.
  nextPress?: boolean;
  // WHICH of the written models is selected. The panel names the model beside the prompt.
  model?: string;
  // THE SIZE IT IS TOLD TO BUILD AT, in the prompt's own words — the next press's beside the
  // button, the run's own in the viewer. See WrittenSize.
  size?: WrittenSize;
  // AND THE PARTS IT IS TOLD IT IS MADE OF — a product's; see WrittenPart.
  parts?: WrittenPart[];
  // AND WHAT THOSE PARTS ARE MADE OF, as this phase's first subphase reads them NOW. Only the
  // next press's row uses it: a pass that has already happened was told what was read then,
  // and shows that instead (see builtWith).
  materials?: Material[];
  // THE CLOSE-UPS THE NEXT PRESS WOULD SEND after the photographs — a product's, where its
  // toggle is on. See productPartCrops.
  crops?: PartCrop[];
  // WHETHER THOSE PICTURES ARE DRAWINGS rather than photographs — a hardware part is modelled
  // from the manual's own schema (see GENERATABLE), and the prompt's opening sentence says so.
  // The panel's whole promise is that this is what goes out.
  schema?: boolean;
  // THE HARDWARE THIS PART WORKS WITH, for a piece of hardware — what the next press sends.
  interactions?: WrittenInteraction[];
}) {
  const { t } = useLanguage();
  const sending = photos ?? [];
  // WHAT THE PANEL IS ASKED TO REDRAW ON. The words come from the server and change with
  // the pictures — one is a sentence, several is a numbered list — so choosing a second
  // photograph has to fetch them again. See CallBadge's own revision prop, which exists for
  // exactly this on the orientation call.
  const key = `${sending.map((p) => `${p.url}|${p.view || ''}`).join('~')}#${JSON.stringify(size ?? null)}#${JSON.stringify(parts ?? null)}#${JSON.stringify(materials ?? null)}#${JSON.stringify(crops ?? null)}#${JSON.stringify(interactions ?? null)}`;

  // THE PASSES, OLDEST FIRST — one row each, numbered as the modal's pass toggle numbers
  // them. A pass with no script is one saved before the script was kept and has nothing to
  // show; the run's own row is always there, so a badge beside a strip it was given no list
  // for still reads that one.
  const all = runs ?? (run ? [run] : []);
  const group = (run ? passGroup(run, all) : [])
    .map((job, i) => ({ job, pass: i + 1, call: writtenCallOf(job) }))
    .filter((one): one is { job: Job3D; pass: number; call: WrittenCall } => !!one.call);
  // AND THE VERSION ON SCREEN, where it is a rebuild: the passes are the calls that were made,
  // and a version is none of them — its row is its own script and what Blender made of it.
  const rootId = run ? versionRootId(run, all) : null;
  const version = run && rebuiltOf(run) ? writtenCallOf(run) : null;
  const versionRow = run && version ? [{
    key: `version${run.id}`,
    icon: <Repeat2 className="h-3.5 w-3.5" />,
    label: t('notice.3d.writtenRebuiltRow')
      .replace('{p}', String(group.findIndex((one) => one.job.id === rootId) + 1 || '?'))
      .replace('{n}', String(versionGroup(run, all).findIndex((j) => j.id === run.id) + 1)),
    panel: <PassOutput job={run} call={version} />,
  }] : [];

  // AND EVERY TRY IT TOOK FOR A PASS TO RUN AT ALL — its error, and the script that raised it,
  // under the pass it belongs to (see openai_blender.fix_script). A run that failed for good
  // carries them too: its pass never saved, they are on the run itself.
  const fixRows = (job: Job3D, pass: number) => fixesOf(job).map((fix, k) => ({
    key: `fix${job.id}-${k}`,
    icon: <Wrench className="h-3.5 w-3.5" />,
    label: t('notice.3d.writtenFixRow').replace('{p}', String(pass)).replace('{n}', String(k + 1)),
    panel: <FixPanel fix={fix} />,
  }));
  const runFixes = run && !run.ready && !group.some((one) => one.job.id === run.id) ? fixRows(run, 1) : [];

  return (
    <CallBadge
      promptLabel={t('notice.3d.writtenPrompt')}
      revision={key}
      load={async () => ({
        ...(await writtenPrompt(subject, model, undefined, sending.map((p) => p.view || ''),
                                size, parts, undefined, materials, crops, undefined, schema,
                                interactions)),
        urls: [
          ...(picker ? [] : sending).map((p, i) => ({
            url: p.url,
            caption: sending.length > 1
              ? `${i + 1}${p.view ? ` · ${directionLabel(p.view)}` : ''}`
              : (p.view ? directionLabel(p.view) : undefined),
          })),
          ...(picker?.crops ? [] : await cropPictures(crops ?? [], sending.length)),
        ],
      })}
      picker={picker?.node}
      // THE PLAIN PROMPT ROW IS THE NEXT PRESS'S, so it goes where the press is. Everywhere
      // else the passes say it better: theirs is the question that was actually put, with the
      // renders that went with it.
      noPrompt={!nextPress}
      title={title}
      named={!!title}
      run={press}
      extra={nextPress ? [...lead, {
        // WHAT EVERY PASS AFTER THE FIRST WILL BE ASKED — the same question for a third look as
        // for a second, so one row.
        key: 'nextCorrection',
        icon: <Repeat2 className="h-3.5 w-3.5" />,
        label: t('notice.3d.writtenNextCorrection'),
        panel: <NextCorrectionPanel subject={subject} model={model} sending={sending} size={size}
                                    parts={parts} materials={materials} crops={crops ?? []}
                                    schema={schema} interactions={interactions} />,
      }] : [...group.flatMap(({ job, pass, call }) => [{
        key: `pass${job.id}`,
        icon: pass === 1 ? <FileText className="h-3.5 w-3.5" /> : <Repeat2 className="h-3.5 w-3.5" />,
        label: t('notice.3d.writtenPassRow').replace('{n}', String(pass)),
        panel: <PassPanel pass={pass} call={call} job={job} subject={subject} model={model}
                          sending={sending} size={size} parts={parts} crops={partCropsOf(job)}
                          schema={schema} />,
      }, ...fixRows(job, pass)]), ...runFixes, ...versionRow]}
    />
  );
}

// ONE PASS, WHOLE: what it was shown and what it wrote back.
//
// The words come from the server, like the button's own prompt and for the same reason — the
// question lives in openai_blender and a copy here would drift from it. The script is added
// on this end because the run is already carrying it and asking twice for something already
// in hand is a request nobody needs.
//
// THE RENDERS ARE THE POINT, for every pass after the first. They are pictures of a mesh that
// no longer exists — the next pass overwrote it — so this panel is the only place an earlier
// attempt can be seen at all, and looking at what a call was shown beside what it wrote is
// how anybody decides whether the extra look earned its money.
// THE CLOSE-UPS AS PICTURES IN THE PANEL, cut here from the photograph and its box — numbered on
// from the photographs, the way the prompt numbers them.
//
// CUT AS THE SERVER CUTS THEM: from the full-size photograph, at its own pixels, neither shrunk
// nor blown up (see notice_3d._cut_part_crops). A thumbnail sharper or blurrier than what the
// model gets is a picture of a different call.
const cropPictures = async (crops: PartCrop[], after: number) => {
  // A piece of hardware's page crops arrive already cut (see usePartStepCrops), and are shown
  // as they are sent.
  const cut = await Promise.all(crops.map((c) => (c.url.startsWith('data:')
    ? c.url : cropOut(listingImageSrc(c.url, true), c.bbox, Infinity, 1))));
  // A ROW PER PART, under its name — `group` is what PromptPanel breaks a row on. The name is
  // the row's heading, so the caption keeps the number and the side.
  return crops.flatMap((c, i) => (cut[i] ? [{
    url: cut[i]!,
    group: c.part,
    caption: `${after + i + 1}${c.view ? ` · ${directionLabel(c.view)}` : ''}`,
  }] : []));
};

function PassPanel({
  pass, call: written, job, subject, model, sending, size, parts, crops, schema,
}: {
  pass: number;
  call: WrittenCall;
  job: Job3D;
  subject?: string;
  model?: string;
  sending: { url: string; view?: string }[];
  size?: WrittenSize;
  parts?: WrittenPart[];
  // The close-ups this run was sent, off the run.
  crops: PartCrop[];
  schema?: boolean;
}) {
  const { t } = useLanguage();
  const draft = written.draft;
  // The renders are filed under the run that WAS SHOWN them, which is this pass — read out
  // here because a narrowed `draft` does not follow into the map below it.
  const draftId = draft?.jobId ?? 0;
  const call = usePromptCall({
    load: async () => {
      // ROUND TWO'S QUESTION FOR EVERY PASS AFTER THE FIRST: the server asks the same thing
      // of a third look as of a second — correct this script, here is what it built — so the
      // flag is "not the first" rather than a pass number.
      // THE MATERIALS THIS PASS WAS TOLD, off its own row and not off the current answer:
      // they can have been read again since, and this panel is what was actually sent.
      const said = await writtenPrompt(subject, model, draft ? 2 : undefined,
        sending.map((p) => p.view || ''), size, parts, draft?.built ?? undefined,
        builtWith(job), crops, undefined, schema, interactionsOfRun(job));
      return {
        ...said,
        prompt: draft ? `${said.prompt}\n\nThe script you wrote:\n\n${draft.script}` : said.prompt,
        urls: [
          ...sending.map((p, i) => ({
            url: p.url,
            caption: sending.length > 1
              ? `${i + 1}${p.view ? ` · ${directionLabel(p.view)}` : ''}`
              : (draft ? t('notice.3d.writtenShotPhoto')
                : (p.view ? directionLabel(p.view) : undefined)),
          })),
          ...await cropPictures(crops, sending.length),
          ...(draft?.shots || []).map((name) => ({
            url: draftShotSrc(draftId, name),
            // The renders follow the photographs' directions now; `side` and `quarter`, the
            // fixed three's names, are no direction and read as themselves.
            caption: t('notice.3d.writtenShotDraft').replace(
              '{v}', DIRECTION_IDS.includes(name) ? directionLabel(name) : name),
          })),
        ],
      };
    },
  });

  // Opened on mount, because the panel only exists once it has been chosen — the menu is
  // the click that would otherwise have to be repeated here.
  //
  // ONCE, and the dependency list says so. `call` is a fresh object every render, so
  // depending on it would run this again after each of the three setStates the fetch
  // lands — harmless today, because open() returns early once there are words, and
  // exactly the kind of harmless that stops being so.
  useEffect(() => {
    call.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <PromptPanel
        label={pass > 1 ? t('notice.3d.writtenSecondLabel') : t('notice.3d.writtenPrompt')}
        call={call}
      />
      <div className="border-t border-slate-200 pt-3">
        <PassOutput job={job} call={written} />
      </div>
    </div>
  );
}

// THE NEXT PRESS'S CORRECTION, before anything is built: the question every pass after the
// first will be asked, from what is selected now. Its renders, the numbers of the build before
// it and that build's script do not exist yet — the words name the renders it will be shown and
// the server stands placeholders where the rest will go.
function NextCorrectionPanel({
  subject, model, sending, size, parts, materials, crops, schema, interactions,
}: {
  subject?: string;
  model?: string;
  sending: { url: string; view?: string }[];
  size?: WrittenSize;
  parts?: WrittenPart[];
  materials?: Material[];
  crops: PartCrop[];
  schema?: boolean;
  interactions?: WrittenInteraction[];
}) {
  const { t } = useLanguage();
  const call = usePromptCall({
    load: async () => ({
      ...(await writtenPrompt(subject, model, 2, sending.map((p) => p.view || ''), size, parts,
                              undefined, materials, crops, true, schema, interactions)),
      urls: [
        ...sending.map((p, i) => ({
          url: p.url,
          caption: `${i + 1}${p.view ? ` · ${directionLabel(p.view)}` : ''}`,
        })),
        ...await cropPictures(crops, sending.length),
      ],
    }),
  });

  // Opened on mount, once — see PassPanel.
  useEffect(() => {
    call.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <PromptPanel label={t('notice.3d.writtenNextCorrection')} call={call} />;
}

// WHAT A PASS WROTE: its reasoning over its script, under a name for the pair. Its own
// seconds with it — `took` on the last pass is the whole request, which over one call's
// reasoning would be minutes that call did not spend.
//
// The whole of a rebuilt pass's panel, which has no question above it to sit under.
function PassOutput({ job, call: written }: { job: Job3D; call: WrittenCall }) {
  const { t } = useLanguage();
  return (
    <>
      {/* WHAT IT FOUND DIFFERENT, before what it thought and wrote — the list a correction opens
          its answer with, part by part: photographs | renders | what it changes. */}
      {written.differences.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 flex items-center justify-center gap-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <ListChecks className="h-3.5 w-3.5" />
            {t('notice.3d.writtenDifferences')}
          </p>
          <ul className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-[11px] leading-relaxed text-slate-800">
            {written.differences.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
      )}
      <p className="mb-3 flex items-center justify-center gap-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <Brain className="h-3.5 w-3.5" />
        {t('notice.3d.writtenAnswer')}
      </p>
      {reasoningTooltip(written.reasoning, written.script, t, stagesOf(job)?.call ?? written.took)}
    </>
  );
}

// ONE TRY AT MAKING A PASS RUN: what Blender stopped on, the script that raised it, and what the
// model thought when asked to fix it — or why it would not answer. See openai_blender.fix_script.
type ScriptFix = { error: string; script: string; reasoning?: string; took?: number; refused?: string };

const fixesOf = (job: Job3D): ScriptFix[] =>
  (Array.isArray(job.params?.fixes) ? job.params.fixes as ScriptFix[] : []);

function FixPanel({ fix }: { fix: ScriptFix }) {
  const { t } = useLanguage();
  const heading = 'mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400';
  const code = 'max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 text-left font-mono text-[11px] leading-relaxed';
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className={heading}>{t('notice.3d.writtenFixError')}</p>
        <pre className={`${code} text-rose-700`}>{fix.error}</pre>
      </div>
      <details>
        <summary className={`${heading} cursor-pointer hover:text-slate-600`}>{t('notice.3d.writtenFixScript')}</summary>
        <pre className={`${code} text-slate-800`}>{fix.script}</pre>
      </details>
      {fix.refused ? (
        <p className="text-center text-sm text-rose-600">{fix.refused}</p>
      ) : fix.reasoning ? (
        <div>
          <p className={`${heading} flex items-center justify-center gap-1.5`}>
            <Brain className="h-3.5 w-3.5" />
            {t('notice.3d.writtenFixThought')}
          </p>
          <p className="whitespace-pre-wrap text-left text-[12px] leading-relaxed text-slate-700">{fix.reasoning}</p>
        </div>
      ) : null}
    </div>
  );
}
